import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Dots from './Dots';
import Background from './Background';
import CornerFrame from './CornerFrame';
import CornerEye from './CornerEye';
import Logo from './Logo';
import Shape from './Shape';
import { useAuth } from '../../../utils/Auth';
import { useWindowSize } from '../../../utils/hooks/useWindowSize';
import QrRef from '../QrRef';
import { AnimatePresence, motion } from 'motion/react';
import { variants, transition, radioVariants, childRadioVariants } from './variants';
import styles from './Style.module.css';

const getDynamicColorStyle = (options) => {
    if (!options) return { background: '#ffffff'};

    if (options.gradient) {
        const { type, colorStops, rotation } = options.gradient;
        const colors = colorStops.map(stop => stop.color).join(', ');

        switch (type) {
            case 'linear':
                const linearDirection = rotation ? `${90 + rotation*60}deg` : 'to right';
                return { background: `linear-gradient(${linearDirection}, ${colors})` };
            case 'radial':
                return { background: `radial-gradient(${colors})` };
            default:
                return { background: `linear-gradient(to right, ${colors})` };
        }
    } else if (options.color) {
        return { backgroundColor: options.color };
    } else {
        return { backgroundColor: '#fff' };
    }
};


// Main Component
export default function Style() {
    const navigate = useNavigate();
    const { userObj, orgObj, qr_options, qr_data } = useAuth();
    const [ display, setDisplay ] = useState('shape');

    const { width } = useWindowSize();
    const size = useMemo(() => {
        if (width <= 500) {
            return 265;
        } else if (width <= 768) {
            return 300;
        } else if (width <= 992) {
            return 250;
        } else {
            return 300;
        }
    },[width]);

    const handleReturn = useMemo(() => {
        switch (display) {
            case 'logo':
                return (
                    <motion.div
                        key='logo'
                        className={styles.motionWrap}
                        transition={transition}
                        variants={variants}
                        initial='initial'
                        animate='animate'
                        exit='initial'
                    >
                        <Logo />
                    </motion.div>
                )
            case 'dots':
                return (
                    <motion.div
                        key='dots'
                        className={styles.motionWrap}
                        transition={transition}
                        variants={variants}
                        initial='initial'
                        animate='animate'
                        exit='initial'
                    >
                        <Dots />
                    </motion.div>
                )
            case 'background':
                return (
                    <motion.div
                        key='background'
                        className={styles.motionWrap}
                        transition={transition}
                        variants={variants}
                        initial='initial'
                        animate='animate'
                        exit='initial'
                    >
                        <Background />
                    </motion.div>
                )
            case 'corner-frame':
                return (
                    <motion.div
                        key='corner-frame'
                        className={styles.motionWrap}
                        transition={transition}
                        variants={variants}
                        initial='initial'
                        animate='animate'
                        exit='initial'
                    >
                        <CornerFrame />
                    </motion.div>
                )
            case 'corner-eye':
                return (
                    <motion.div
                        key='corner-eye'
                        className={styles.motionWrap}
                        transition={transition}
                        variants={variants}
                        initial='initial'
                        animate='animate'
                        exit='initial'
                    >
                        <CornerEye />
                    </motion.div>
                )
            case 'shape':
            default: 
                return (
                    <motion.div
                        key='shape'
                        className={styles.motionWrap}
                        transition={transition}
                        variants={variants}
                        initial='initial'
                        animate='animate'
                        exit='initial'
                    >
                        <Shape />
                    </motion.div>
                )
        }
    },[display]);

    if (userObj?.plan?.plan_id === 0 && qr_data?.id?.includes('-')) {
        return (
            <div className={styles.upgradeContainer}>
                <div className={styles.upgrade} onClick={() => navigate('/organizations/settings')}>Uprade</div> to customize your QR
            </div>
        )
    };

    if (qr_options) {
        return (
            <div className={styles.container}>
                <div className={styles.left}>
                    <h5 className={styles.header}>Customization</h5>
                    <RadioBtns qr_options={qr_options} display={display} setDisplay={setDisplay} width={width} />
                    <AnimatePresence mode='wait'>
                        {handleReturn}
                    </AnimatePresence>
                </div>
                <div className={styles.right}>
                    <div className={styles.qrWrap}>
                        {!(orgObj?.plan?.plan_id === 0 && qr_data?.id?.includes('-')) &&
                            <QrRef size={size} />
                        }
                    </div>
                </div>
                <div className={styles.blue} />
            </div>
        );
    };
};

const RadioBtns = ({qr_options, display, setDisplay}) => (
    <div className={styles.radioContainer}>
        <label className={styles.radio}>
            <input type='radio' className={styles.input} onChange={e => setDisplay(e.target.id)} id='shape' checked={display === 'shape'} />
            Shape
            <span className={qr_options?.shape === 'square' ? styles.square : `${styles.square} ${styles.circle}`} />
        </label>
        <label 
            className={styles.radio}>
            <input type='radio' className={styles.input} onChange={e => setDisplay(e.target.id)} id='dots' checked={display === 'dots'} />
            Dots
            <span className={styles.color} style={getDynamicColorStyle(qr_options?.dotsOptions)} />
        </label>
        <label 
            className={styles.radio}>
            <input type='radio' className={styles.input} onChange={e => setDisplay(e.target.id)} id='background' checked={display === 'background'} />
            Background
            <span className={styles.color} style={getDynamicColorStyle(qr_options?.backgroundOptions)} />
        </label>
        <label 
            className={styles.radio}>
            <input type='radio' className={styles.input} onChange={e => setDisplay(e.target.id)} id='corner-frame' checked={display === 'corner-frame'} />
            Corner Frame
            <span className={styles.color} style={getDynamicColorStyle(qr_options?.cornersSquareOptions)} />
        </label>
        <label 
            className={styles.radio}>
            <input type='radio' className={styles.input} onChange={e => setDisplay(e.target.id)} id='corner-eye' checked={display === 'corner-eye'} />
            Corner Eye
            <span className={styles.color} style={getDynamicColorStyle(qr_options?.cornersDotOptions)} />
        </label>
        <label 
            className={styles.radio}>
            <input type='radio' className={styles.input} onChange={e => setDisplay(e.target.id)} id='logo' checked={display === 'logo'} />
            Logo
            <span className={styles.logo}>{qr_options?.image ? <i className='far fa-image' /> : <i className='far fa-image-slash' />}</span>
        </label>
    </div>
);